/**
 * Admin Panel for AI Usage Management
 * Simple interface for toggling limits and viewing analytics
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ToggleLeft, ToggleRight, Settings } from 'lucide-react';
import { AIUsageService } from '../lib/aiUsageService';
import { useNoshStore } from '../lib/store';

interface AdminAIUsagePanelProps {
  className?: string;
}

export function AdminAIUsagePanel({ className = '' }: AdminAIUsagePanelProps) {
  const { user } = useNoshStore();
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load current settings
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load global settings (public endpoint)
      const settingsResult = await AIUsageService.getGlobalSettings();
      if (settingsResult.success) {
        setSettings(settingsResult.settings);
      }
    } catch (err) {
      setError('Failed to load admin data');
      console.error('Error loading admin data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleLimits = async () => {
    if (!user || !settings) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const newState = !settings.limits_enabled;
      const result = await AIUsageService.adminToggleLimits(
        user.id || user.user_id,
        newState,
        `Toggled via admin panel at ${new Date().toISOString()}`
      );

      if (result.success) {
        setSuccessMessage(result.message);
        // Reload data to reflect changes
        await loadData();
      } else {
        setError(result.message || 'Failed to toggle limits');
      }
    } catch (err) {
      setError('Exception occurred while toggling limits');
      console.error('Error toggling limits:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  if (!user) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <p className="text-red-600 text-sm">❌ Admin panel requires authentication</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-xl p-4">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6" />
          <div>
            <h2 className="text-xl font-bold">AI Usage Admin Panel</h2>
            <p className="text-purple-100 text-sm">Control global AI limits and view analytics</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Loading admin data...</span>
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between"
          >
            <p className="text-red-600 text-sm">❌ {error}</p>
            <button 
              onClick={clearMessages}
              className="text-red-400 hover:text-red-600 text-sm"
            >
              ✕
            </button>
          </motion.div>
        )}

        {successMessage && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between"
          >
            <p className="text-green-600 text-sm">✅ {successMessage}</p>
            <button 
              onClick={clearMessages}
              className="text-green-400 hover:text-green-600 text-sm"
            >
              ✕
            </button>
          </motion.div>
        )}

        {/* Global Settings Control */}
        {settings && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <ToggleLeft className="w-5 h-5" />
              Global AI Limits Control
            </h3>
            
            <div className="space-y-4">
              {/* Main Toggle */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                <div>
                  <h4 className="font-medium text-gray-800">AI Usage Limits</h4>
                  <p className="text-sm text-gray-600">
                    {settings.limits_enabled 
                      ? 'Users are restricted by their plan limits'
                      : 'All users have unlimited access (tracking still active)'
                    }
                  </p>
                </div>
                <button
                  onClick={handleToggleLimits}
                  disabled={isLoading}
                  className={`p-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                    settings.limits_enabled
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  } disabled:opacity-50`}
                >
                  {settings.limits_enabled ? (
                    <>
                      <ToggleRight className="w-6 h-6" /> 
                      <span>ENABLED</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-6 h-6" /> 
                      <span>DISABLED</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">🎯 For Judging Period</h3>
          <p className="text-sm text-yellow-700 mb-3">
            AI limits are currently <strong>{settings?.limits_enabled ? 'ENABLED' : 'DISABLED'}</strong>. 
            {settings?.limits_enabled 
              ? ' Users are restricted by plan limits. Toggle OFF for unlimited testing.'
              : ' All users have unlimited access. Perfect for judging and testing!'
            }
          </p>
          <button 
            onClick={loadData}
            className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-yellow-700 transition-colors"
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
} 